// /src/reports/SnappyDeliveriesReport.js
import React, { useState, useEffect } from "react";
import { supabase } from "../pages/supabaseClient";
import { saveAs } from "file-saver";
import { format, addDays } from "date-fns";
import { toast } from "react-toastify";

// OneStop week start date
const ONE_STOP_WEEK1_START = new Date("2025-02-23"); // Sunday

function getOneStopWeekInfo(date) {
  const diffDays = Math.floor(
    (date - ONE_STOP_WEEK1_START) / (1000 * 60 * 60 * 24)
  );
  const weekNum = Math.floor(diffDays / 7) + 1;
  const weekStart = addDays(ONE_STOP_WEEK1_START, (weekNum - 1) * 7);
  const weekEnd = addDays(weekStart, 6);
  return { weekNum, weekStart, weekEnd };
}

export default function SnappyDeliveriesReport({ fromDate, toDate }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewType, setViewType] = useState("daily");

  useEffect(() => {
    fetchData();
  }, [fromDate, toDate, viewType]);

  async function fetchData() {
    setLoading(true);

    const { data, error } = await supabase
      .from("clockins")
      .select("*")
      .gte("date", fromDate)
      .lte("date", toDate);

    if (error) {
      toast.error("Error fetching Snappy deliveries data");
      setLoading(false);
      return;
    }

    const processed = data.map((c) => {
      const dateStr = format(new Date(c.date), "yyyy-MM-dd");

      const onShift = parseInt(c.on_shift_deliveries) || 0;
      const offShift = parseInt(c.off_shift_deliveries) || 0;
      const free = parseInt(c.free_deliveries) || 0;
      const totalDeliveries = onShift + offShift;
      const deliveryFee = parseFloat(c.delivery_fee) || 0;

      return {
        date: dateStr,
        employee: c.employee || "",
        onShift,
        offShift,
        free,
        totalDeliveries,
        deliveryFee
      };
    });

    setRows(processed);
    setLoading(false);
  }

  function groupByWeek(data) {
    const grouped = {};
    data.forEach((row) => {
      const { weekNum, weekStart, weekEnd } = getOneStopWeekInfo(
        new Date(row.date)
      );
      const key = `Week ${weekNum} (${format(weekStart, "dd MMM")} - ${format(
        weekEnd,
        "dd MMM"
      )})`;
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(row);
    });
    return grouped;
  }

  function exportCSV() {
    const headers = [
      "Date/Week",
      "Employee",
      "On-Shift Deliveries",
      "Off-Shift Deliveries",
      "Free Deliveries",
      "Total Deliveries",
      "Delivery Fee (£)"
    ];
    let csvRows = [headers.join(",")];

    if (viewType === "daily") {
      rows.forEach((r) => {
        csvRows.push([
          r.date,
          r.employee,
          r.onShift,
          r.offShift,
          r.free,
          r.totalDeliveries,
          r.deliveryFee.toFixed(2)
        ].join(","));
      });
    } else {
      const weeklyGroups = groupByWeek(rows);
      Object.keys(weeklyGroups).forEach((week) => {
        weeklyGroups[week].forEach((r) => {
          csvRows.push([
            week,
            r.employee,
            r.onShift,
            r.offShift,
            r.free,
            r.totalDeliveries,
            r.deliveryFee.toFixed(2)
          ].join(","));
        });
      });
    }

    saveAs(
      new Blob([csvRows.join("\n")], { type: "text/csv" }),
      `snappy_deliveries_${format(new Date(), "yyyyMMdd_HHmm")}.csv`
    );
  }

  if (loading) return <p>Loading Snappy Deliveries Report...</p>;

  if (viewType === "daily") {
    const totals = rows.reduce(
      (acc, r) => {
        acc.onShift += r.onShift;
        acc.offShift += r.offShift;
        acc.free += r.free;
        acc.totalDeliveries += r.totalDeliveries;
        acc.deliveryFee += r.deliveryFee;
        return acc;
      },
      { onShift: 0, offShift: 0, free: 0, totalDeliveries: 0, deliveryFee: 0 }
    );

    return (
      <div>
        <label>View Type: </label>
        <select
          value={viewType}
          onChange={(e) => setViewType(e.target.value)}
        >
          <option value="daily">Daily</option>
          <option value="weekly">Weekly</option>
        </select>
        <button onClick={exportCSV} style={{ marginLeft: "10px" }}>
          Export CSV
        </button>

        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Employee</th>
              <th>On-Shift</th>
              <th>Off-Shift</th>
              <th>Free</th>
              <th>Total Deliveries</th>
              <th>Delivery Fee (£)</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td>{r.date}</td>
                <td>{r.employee}</td>
                <td>{r.onShift}</td>
                <td>{r.offShift}</td>
                <td>{r.free}</td>
                <td>{r.totalDeliveries}</td>
                <td>{r.deliveryFee.toFixed(2)}</td>
              </tr>
            ))}
            <tr style={{ fontWeight: "bold", background: "#f0f0f0" }}>
              <td colSpan="2">Total</td>
              <td>{totals.onShift}</td>
              <td>{totals.offShift}</td>
              <td>{totals.free}</td>
              <td>{totals.totalDeliveries}</td>
              <td>{totals.deliveryFee.toFixed(2)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  } else {
    const weeklyGroups = groupByWeek(rows);
    const grandTotals = rows.reduce(
      (acc, r) => {
        acc.onShift += r.onShift;
        acc.offShift += r.offShift;
        acc.free += r.free;
        acc.totalDeliveries += r.totalDeliveries;
        acc.deliveryFee += r.deliveryFee;
        return acc;
      },
      { onShift: 0, offShift: 0, free: 0, totalDeliveries: 0, deliveryFee: 0 }
    );

    return (
      <div>
        <label>View Type: </label>
        <select
          value={viewType}
          onChange={(e) => setViewType(e.target.value)}
        >
          <option value="daily">Daily</option>
          <option value="weekly">Weekly</option>
        </select>
        <button onClick={exportCSV} style={{ marginLeft: "10px" }}>
          Export CSV
        </button>

        {Object.keys(weeklyGroups).map((week) => {
          const weekRows = weeklyGroups[week];
          const weekTotals = weekRows.reduce(
            (acc, r) => {
              acc.onShift += r.onShift;
              acc.offShift += r.offShift;
              acc.free += r.free;
              acc.totalDeliveries += r.totalDeliveries;
              acc.deliveryFee += r.deliveryFee;
              return acc;
            },
            { onShift: 0, offShift: 0, free: 0, totalDeliveries: 0, deliveryFee: 0 }
          );

          return (
            <div key={week} style={{ marginBottom: "20px" }}>
              <h3>{week}</h3>
              <table>
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>On-Shift</th>
                    <th>Off-Shift</th>
                    <th>Free</th>
                    <th>Total Deliveries</th>
                    <th>Delivery Fee (£)</th>
                  </tr>
                </thead>
                <tbody>
                  {weekRows.map((r, i) => (
                    <tr key={i}>
                      <td>{r.employee}</td>
                      <td>{r.onShift}</td>
                      <td>{r.offShift}</td>
                      <td>{r.free}</td>
                      <td>{r.totalDeliveries}</td>
                      <td>{r.deliveryFee.toFixed(2)}</td>
                    </tr>
                  ))}
                  <tr style={{ fontWeight: "bold", background: "#f0f0f0" }}>
                    <td>Total</td>
                    <td>{weekTotals.onShift}</td>
                    <td>{weekTotals.offShift}</td>
                    <td>{weekTotals.free}</td>
                    <td>{weekTotals.totalDeliveries}</td>
                    <td>{weekTotals.deliveryFee.toFixed(2)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          );
        })}

        <h3 style={{ marginTop: "10px" }}>
          Grand Total Deliveries: {grandTotals.totalDeliveries} | Total Fee: £{grandTotals.deliveryFee.toFixed(2)}
        </h3>
      </div>
    );
  }
}
